import React, { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiGrid,
  FiBriefcase,
  FiPlus,
  FiActivity,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiAward,
  FiStar,
  FiList,
  FiClipboard,
  FiFilePlus,
  FiFileText,
  FiHeart,
  FiMail,
  FiCompass,
  FiCreditCard,
  FiShare2,
  FiTag,
  FiShield,
  FiCalendar,
  FiClock,
  FiLogOut,
  FiX,
  FiMenu,
} from "react-icons/fi";

const Adminsidebar = ({ isMobile, onLinkClick, isCollapsed = false, setIsCollapsed }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeItem, setActiveItem] = useState("/recruitment-dashboard");

  const navigate = useNavigate();
  const location = useLocation();

  // Detect active page from location
  useEffect(() => {
    const path = location.pathname;
    setActiveItem(path);

    // Auto-open parent dropdown if active route is a child
    elements.forEach((item) => {
      if (item.dropdown && item.dropdown.some((sub) => sub.path === path)) {
        setOpenDropdown(item.name);
      }
    });
  }, [location.pathname]);

  const toggleDropdown = (e, name) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/client-login");
  };

  const isActive = (path) => activeItem === path;

  const isDropdownActive = (dropdownItems) =>
    dropdownItems?.some((item) => isActive(item.path));

  const elements = [
    {
      icon: <FiGrid size={16} />,
      name: "Dashboard",
      path: "/recruitment-dashboard",
    },
    {
      icon: <FiBriefcase size={16} />,
      name: "Job Openings",
      dropdown: [
        { name: "All Job Posts", path: "/job-post" },
        { name: "Post New Job", path: "/post-new-job" },
        { name: "Position Status", path: "/job-positions-status" },
      ],
    },
    {
      icon: <FiUsers size={16} />,
      name: "Candidates",
      dropdown: [
        { name: "All Applicants", path: "/job-applicants" },
        { name: "Short Applicants", path: "/short-job-applicants" },
        { name: "Score Board", path: "/score" },
        { name: "Schedule Interviews", path: "/schedule-interviews" },
        { name: "Selected Hires", path: "/selected-candidates" },
        { name: "Rejected", path: "/rejected-candidates" },
      ],
    },
    {
      icon: <FiStar size={16} />,
      name: "Short Gigs",
      dropdown: [
        { name: "Post Short Job", path: "/short-jobs" },
        { name: "Posted Short Jobs", path: "/posted-short-jobs" },
      ],
    },
    {
      icon: <FiClipboard size={16} />,
      name: "Assessments",
      dropdown: [
        { name: "All Assessments", path: "/assessment-manager" },
        { name: "Create Assessment", path: "/new-assessment" },
      ],
    },
    {
      icon: <FiFileText size={16} />,
      name: "Documents",
      dropdown: [
        { name: "Personal Docs", path: "/personal-documents" },
        { name: "Offer Letters", path: "/acceptance-letter" },
        { name: "Staff Letters", path: "/employee-letters" },
        { name: "Resignations", path: "/employee-resignation" },
        { name: "Medical Certs", path: "/all-medical-certificates" },
      ],
    },
    {
      icon: <FiCompass size={16} />,
      name: "Engagement",
      path: "/employee-journey",
    },
    {
      icon: <FiCreditCard size={16} />,
      name: "Wallet & Perks",
      dropdown: [
        { name: "My Wallet", path: "/mywallet" },
        { name: "Refer & Earn", path: "/myreffral" },
        { name: "Special Offers", path: "/specialoffers" },
      ],
    },
    {
      icon: <FiShield size={16} />,
      name: "Page Access",
      path: "/page-access",
    },
    {
      icon: <FiCalendar size={16} />,
      name: "Events",
      path: "/events",
    },
    {
      icon: <FiClock size={16} />,
      name: "Attendance",
      path: "/attendancelist",
    },
  ];

  const handleItemClick = (path, action) => {
    if (path) {
      navigate(path);
    }
    if (action) {
      action();
    }
    if (isMobile && setIsCollapsed) {
      setIsCollapsed(true);
    }
    if (onLinkClick) {
      onLinkClick();
    }
  };

  const handleDropdownItemClick = (path) => {
    navigate(path);
    if (isMobile && setIsCollapsed) {
      setIsCollapsed(true);
    }
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <>
      {isMobile && !isCollapsed && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-xs"
          onClick={() => {
            if (setIsCollapsed) setIsCollapsed(true);
          }}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen bg-white border-r border-slate-200 z-50 transition-transform duration-200 shadow-sm flex flex-col justify-between select-none ${
          isMobile
            ? isCollapsed
              ? "-translate-x-full w-[220px]"
              : "translate-x-0 w-[220px]"
            : "w-[220px]"
        }`}
      >
        {/* Header */}
        <div className="flex items-center px-4 border-b border-slate-100 h-14 shrink-0 bg-slate-50/50">
          <div className="flex items-center gap-2.5 w-full min-w-0">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-600 text-white flex items-center justify-center font-black text-xs shrink-0 shadow-md shadow-indigo-600/20">
              TH
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-black text-slate-900 tracking-tight block truncate">
                TIMELY HEALTH
              </span>
              <span className="text-[9px] uppercase tracking-widest font-black text-indigo-600 block">
                Recruiter Portal
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Menu List with independent scroll */}
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto custom-scrollbar">
          {elements.map((item, idx) => {
            const hasActiveChild = isDropdownActive(item.dropdown);
            const isSelfActive = isActive(item.path);
            const isParentOpen = openDropdown === item.name;

            return (
              <div key={idx}>
                {item.dropdown ? (
                  <>
                    {/* Dropdown Header Item */}
                    <div
                      className={`group flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all duration-150 ${
                        hasActiveChild
                          ? "bg-indigo-600 text-white shadow-sm shadow-indigo-600/25"
                          : isParentOpen
                          ? "bg-indigo-50 text-indigo-800"
                          : "text-slate-800 hover:text-indigo-700 hover:bg-indigo-50/60"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.dropdown && item.dropdown.length > 0) {
                          handleDropdownItemClick(item.dropdown[0].path);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          className={`shrink-0 transition-colors ${
                            hasActiveChild
                              ? "text-white"
                              : isParentOpen
                              ? "text-indigo-700"
                              : "text-slate-500 group-hover:text-indigo-600"
                          }`}
                        >
                          {item.icon}
                        </span>
                        <span
                          className={`text-xs font-black leading-tight truncate ${
                            hasActiveChild
                              ? "text-white font-black"
                              : isParentOpen
                              ? "text-indigo-800 font-black"
                              : "text-slate-800 group-hover:text-indigo-700 font-black"
                          }`}
                        >
                          {item.name}
                        </span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <FaChevronDown
                          onClick={(e) => toggleDropdown(e, item.name)}
                          className={`text-[10px] transition-transform duration-200 p-0.5 rounded cursor-pointer ${
                            hasActiveChild
                              ? "text-white"
                              : isParentOpen
                              ? "text-indigo-700 rotate-180"
                              : "text-slate-400 group-hover:text-indigo-600"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Dropdown Children */}
                    {isParentOpen && (
                      <ul className="mt-1 mb-1 space-y-0.5 pl-3 border-l-2 border-indigo-100 ml-3.5 animate-in fade-in duration-150">
                        {item.dropdown.map((sub, i) => {
                          const isSubActive = isActive(sub.path);
                          return (
                            <li key={i}>
                              <Link
                                to={sub.path}
                                onClick={() => handleDropdownItemClick(sub.path)}
                                className={`flex items-center gap-2 py-1.5 px-2.5 rounded-lg text-xs font-black transition-colors ${
                                  isSubActive
                                    ? "bg-indigo-50 text-indigo-700 font-black"
                                    : "text-slate-700 hover:text-indigo-700 hover:bg-indigo-50/60 font-black"
                                }`}
                              >
                                {isSubActive && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0"></div>
                                )}
                                <span className="truncate">{sub.name}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  /* Single Navigation Item */
                  <div
                    onClick={() => handleItemClick(item.path, item.action)}
                    className={`group flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all duration-150 ${
                      isSelfActive
                        ? "bg-indigo-600 text-white shadow-sm shadow-indigo-600/25"
                        : "text-slate-800 hover:text-indigo-700 hover:bg-indigo-50/60"
                    }`}
                  >
                    <span
                      className={`shrink-0 transition-colors ${
                        isSelfActive
                          ? "text-white"
                          : "text-slate-500 group-hover:text-indigo-600"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span
                      className={`text-xs font-black leading-tight truncate flex-1 ${
                        isSelfActive
                          ? "text-white font-black"
                          : "text-slate-800 group-hover:text-indigo-700 font-black"
                      }`}
                    >
                      {item.name}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-100 bg-slate-50/50 shrink-0 space-y-2">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-slate-800 hover:text-rose-600 hover:bg-rose-50 transition-colors text-xs font-black cursor-pointer"
          >
            <FiLogOut size={15} className="text-slate-400 group-hover:text-rose-600" />
            <span>Sign Out</span>
          </button>
          <div className="px-1 text-[9px] font-black text-slate-400 uppercase tracking-widest text-center">
            © 2026 Timely Health
          </div>
        </div>
      </aside>
    </>
  );
};

export default Adminsidebar;